import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '@/hooks/useAuth'
import { useResponsive } from '@/hooks/useResponsive'
import { api } from '@/api/axios'
import { BottomNavigator } from '@/components/BottomNavigator'
import { colors, spacing } from '@/theme'
import {
  ArrowLeft,
  Edit,
  Users,
  Star,
  Award,
  Share2,
  LogOut,
} from 'lucide-react'

export function ProfileScreen() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { maxWidth, isDesktop } = useResponsive()
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
  const referralCode = user?.referralCode || (user?.email ? `WD-${user.email.split('@')[0].toUpperCase()}` : 'WD-USER')
  const profileImageUrl = (() => {
    if (!user?.profileImage) return '/src/assets/perfil.jpeg'
    if (user.profileImage.startsWith('http://') || user.profileImage.startsWith('https://')) {
      return user.profileImage
    }
    const baseURL = api.defaults.baseURL || ''
    const apiRoot = baseURL.replace(/\/api\/v1\/?$/, '')
    const path = user.profileImage.startsWith('/') ? user.profileImage : `/${user.profileImage}`
    return `${apiRoot}${path}`
  })()

  const handleLogout = () => {
    logout()
    navigate('/auth')
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  }

  return (
    <>
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: colors.primary[900],
          backgroundImage: `radial-gradient(circle at 20% 50%, rgba(37, 99, 235, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(30, 41, 59, 0.1) 0%, transparent 50%)`,
          padding: isDesktop ? spacing[6] : spacing[4],
          paddingBottom: '56px',
        }}
      >
        <div style={{ maxWidth: maxWidth, margin: '0 auto' }}>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing[4],
            }}
          >
            {/* Header con botón atrás */}
            <motion.div
              variants={itemVariants}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => navigate('/dashboard')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.white,
                  fontSize: '24px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing[2],
                }}
              >
                <ArrowLeft size={24} />
                Atrás
              </motion.button>
              <h1
                style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: colors.white,
                }}
              >
                Mi Perfil
              </h1>

            </motion.div>

            {/* Tarjeta de Perfil Principal */}
            <motion.div
              variants={itemVariants}
              style={{
                background: `linear-gradient(135deg, ${colors.primary[500]} 0%, #3b82f6 50%, ${colors.primary[700]} 100%)`,
                borderRadius: '24px',
                padding: spacing[6],
                color: colors.white,
                boxShadow: '0 20px 40px rgba(37, 99, 235, 0.3)',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: spacing[4],
                textAlign: 'center',
              }}
            >
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.1, 0.2, 0.1],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                style={{
                  position: 'absolute',
                  top: -50,
                  right: -50,
                  width: 200,
                  height: 200,
                  borderRadius: '50%',
                  background: colors.white,
                }}
              />

              <div style={{ position: 'relative', zIndex: 1 }}>
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  style={{
                    position: 'relative',
                  }}
                >
                  <img
                    src={profileImageUrl}
                    alt="Perfil"
                    style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: `5px solid ${colors.white}`,
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: colors.success[500],
                      border: `4px solid ${colors.white}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                    }}
                  >
                    ✓
                  </div>
                </motion.div>
              </div>

              <div style={{ position: 'relative', zIndex: 1 }}>
                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%',
                    padding: '8px 20px',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    border: '1.5px solid rgba(255,255,255,0.3)',
                    borderRadius: '10px',
                    color: '#fca5a5',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: spacing[1],
                    marginBottom: spacing[3],
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.borderColor = '#fca5a5' }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)' }}
                >
                  <LogOut size={16} />
                  Salir
                </button>
                <h2
                  style={{
                    fontSize: '28px',
                    fontWeight: 700,
                    marginBottom: spacing[1],
                  }}
                >
                  {fullName || 'Usuario'}
                </h2>
                <p
                  style={{
                    fontSize: '14px',
                    opacity: 0.9,
                    marginBottom: spacing[3],
                  }}
                >
                  {user?.email}
                </p>

                {/* Badges de Estrellas */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: spacing[2],
                    marginBottom: spacing[4],
                  }}
                >
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={28}
                      fill={i < 4 ? colors.warning[500] : 'rgba(255,255,255,0.3)'}
                      color={colors.warning[500]}
                    />
                  ))}
                </div>

                <p style={{ fontSize: '12px', opacity: 0.85 }}>
                  {user?.referralsCount || 0} REFERIDOS • {user?.isActive ? 'ACTIVO' : 'INACTIVO'}
                </p>
              </div>
            </motion.div>

            {/* Stats de Perfil */}
            <motion.div
              variants={itemVariants}
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: spacing[3],
              }}
            >
              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ type: 'spring', stiffness: 300 }}
                style={{
                  backgroundColor: colors.white,
                  borderRadius: '16px',
                  padding: spacing[4],
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                  border: `1.5px solid ${colors.primary[200]}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: spacing[2],
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '12px',
                    backgroundColor: colors.primary[100],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Award size={24} color={colors.primary[500]} />
                </div>
                <div>
                  <p style={{ fontSize: '20px', fontWeight: 700, color: colors.gray[900] }}>
                  {user?.referralsCount || 0}
                  </p>
                  <p style={{ fontSize: '12px', color: colors.gray[600] }}>
                    Referidos
                  </p>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ type: 'spring', stiffness: 300 }}
                style={{
                  backgroundColor: colors.white,
                  borderRadius: '16px',
                  padding: spacing[4],
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                  border: `1.5px solid ${colors.primary[200]}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: spacing[2],
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '12px',
                    backgroundColor: `${colors.primary[500]}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Star size={24} color={colors.warning[500]} />
                </div>
                <div>
                  <p style={{ fontSize: '20px', fontWeight: 700, color: colors.gray[900] }}>
                    4.0
                  </p>
                  <p style={{ fontSize: '12px', color: colors.gray[600] }}>
                    Calificación
                  </p>
                </div>
              </motion.div>
            </motion.div>

            {/* Tarjetas de Acciones */}
            <motion.div
              variants={itemVariants}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing[3],
              }}
            >
              <h3
                style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: colors.white,
                  padding: `0 ${spacing[2]}`,
                }}
              >
                Acciones
              </h3>

              {/* Editar Perfil */}
              <motion.div
                whileHover={{ scale: 1.02, x: 5 }}
                style={{
                  backgroundColor: colors.white,
                  borderRadius: '16px',
                  padding: spacing[4],
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                  border: `1.5px solid ${colors.primary[200]}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing[3],
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/wizard')}
              >
                <div
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '12px',
                    backgroundColor: colors.primary[100],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Edit size={24} color={colors.primary[500]} />
                </div>
                <div style={{ flex: 1 }}>
                  <h4
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: colors.gray[900],
                      marginBottom: spacing[1],
                    }}
                  >
                    Editar mi Información
                  </h4>
                  <p
                    style={{
                      fontSize: '12px',
                      color: colors.gray[600],
                    }}
                  >
                    Actualiza tu CV y datos profesionales
                  </p>
                </div>
              </motion.div>

              {/* Compartir CV */}
              <motion.div
                whileHover={{ scale: 1.02, x: 5 }}
                style={{
                  backgroundColor: colors.white,
                  borderRadius: '16px',
                  padding: spacing[4],
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                  border: `1.5px solid ${colors.primary[200]}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing[3],
                  cursor: 'pointer',
                }}
                onClick={() => navigator.clipboard.writeText(user?.email || '')}
              >
                <div
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '12px',
                    backgroundColor: `${colors.primary[500]}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Share2 size={24} color={colors.primary[500]} />
                </div>
                <div style={{ flex: 1 }}>
                  <h4
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: colors.gray[900],
                      marginBottom: spacing[1],
                    }}
                  >
                    Compartir CV
                  </h4>
                  <p
                    style={{
                      fontSize: '12px',
                      color: colors.gray[600],
                    }}
                  >
                    Comparte tu perfil con reclutadores
                  </p>
                </div>
              </motion.div>

              {/* Invitar Amigos */}
              <motion.div
                whileHover={{ scale: 1.02, x: 5 }}
                style={{
                  backgroundColor: colors.white,
                  borderRadius: '16px',
                  padding: spacing[4],
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                  border: `1.5px solid ${colors.primary[200]}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing[3],
                  cursor: 'pointer',
                }}
                onClick={() => {
                  navigator.clipboard.writeText(referralCode)
                }}
              >
                <div
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '12px',
                    backgroundColor: colors.success[50],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Users size={24} color={colors.success[500]} />
                </div>
                <div style={{ flex: 1 }}>
                  <h4
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: colors.gray[900],
                      marginBottom: spacing[1],
                    }}
                  >
                    Invitar Amigos
                  </h4>
                  <p
                    style={{
                      fontSize: '12px',
                      color: colors.gray[600],
                    }}
                  >
                    Gana estrellas invitando conductores
                  </p>
                </div>
              </motion.div>
            </motion.div>


          </motion.div>
        </div>
      </div>
      <BottomNavigator />
    </>
  )
}
